import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { Lock } from 'react-feather';
import usePermissions from '../../hooks/usePermissions';

/**
 * Componente para proteger rotas baseado em permissões
 * Redireciona para página de acesso negado se não tiver permissão
 */
const ProtectedRoute = ({
  children,
  recurso,
  acao = 'read',
  modulo,
  redirectTo = '/acesso-negado',
  showLoading = true
}) => {
  const { hasPermission, hasModulePermission, loading } = usePermissions();

  // Mostrar loading enquanto carrega permissões
  if (loading && showLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}
      >
        <CircularProgress size={50} />
        <Typography variant="body1" color="textSecondary">
          Verificando permissões...
        </Typography>
      </Box>
    );
  }

  // Verificar permissão de módulo
  if (modulo && !hasModulePermission(modulo)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Verificar permissão específica de recurso
  if (recurso && !hasPermission(recurso, acao)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Tem permissão - renderizar conteúdo
  return <>{children}</>;
};

/**
 * Página de Acesso Negado
 */
export const AcessoNegado = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 5,
          maxWidth: 500,
          textAlign: 'center',
          borderTop: '4px solid #d32f2f'
        }}
      >
        <Lock
          size={64}
          style={{
            color: '#d32f2f',
            marginBottom: 16
          }}
        />
        <Typography variant="h4" gutterBottom color="error">
          Acesso Negado
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Você não tem permissão para acessar esta página.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Entre em contato com o administrador do sistema para solicitar acesso.
        </Typography>
      </Paper>
    </Box>
  );
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  recurso: PropTypes.string,
  acao: PropTypes.string,
  modulo: PropTypes.string,
  redirectTo: PropTypes.string,
  showLoading: PropTypes.bool
};

export default ProtectedRoute;
